import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import numeral from 'numeral'
import { useSelector } from 'react-redux'
import moment from 'moment'

import styles from './styles.module.scss'

import { getCurrency } from '../../../../helpers/currencies'

const formatNumber = (value, currency) => {
  return `${numeral(value).format('0,0.[00]')} ${currency}`
}

const RevenueByCategoryTips = ({transactions, from, to}) => {
  const { t } = useTranslation()
  const companyInfo = useSelector((state) => state.company.companyInfo)
  const currency = getCurrency(companyInfo?.currency)

  const data = useMemo(() => {
    const invoices = transactions.filter(tr => tr.TransactionType === 'INVOICE')
    const totalAmount = invoices.reduce((acc, transaction) => acc + (transaction['Sum.Incoming'] || 0), 0)
    const supplierMap = invoices.reduce((acc, transaction) => {
      const supplierName = transaction['Counteragent.Name'] || 'Uncategorized'
      return {
        ...acc,
        [supplierName]: acc[supplierName] ? [...acc[supplierName], transaction] : [transaction],
      }
    }, {})
    const suppliers = Object.entries(supplierMap).map(([supplier, transactions]) => {
      const amount = transactions.reduce((acc, transaction) => acc + (transaction['Sum.Incoming'] || 0), 0)
      return {
        name: supplier,
        amount: amount,
        percentage: totalAmount > 0 ? (amount / totalAmount * 100) : 0,
      }
    }).sort((a, b) => b.amount - a.amount)
    const categoryMap = invoices.reduce((acc, transaction) => {
      const categoryName = transaction['Product.TopParent'] || 'Uncategorized'
      return {
        ...acc,
        [categoryName]: acc[categoryName] ? [...acc[categoryName], transaction] : [transaction],
      }
    }, {})
    const categories = Object.entries(categoryMap).map(([category, transactions]) => {
      const amount = transactions.reduce((acc, transaction) => acc + (transaction['Sum.Incoming'] || 0), 0)
      return {
        name: category,
        amount: amount,
        percentage: totalAmount > 0 ? (amount / totalAmount * 100) : 0,
      }
    }).sort((a, b) => b.amount - a.amount)

    const [largestSupplier] = suppliers
    const [largestCategory] = categories

    return {
      totalAmount: totalAmount,
      invoices: invoices.length,
      largestSupplier: largestSupplier,
      largestCategory: largestCategory,
    }
  }, [transactions])

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.title}>{t('Income statement')}</div>
      </div>
      <div className={styles.tips}>
        <div dangerouslySetInnerHTML={{
          __html: t('From {{from}} to {{to}}, <b>{{totalAmount}}</b> worth of goods were purchased under <b>{{invoices}}</b> purchase invoices. Largest supplier: <b>{{largestSupplier}} — {{largestSupplierAmount}}</b> ({{largestSupplierPercentage}}% of total revenue). Largest category: <b>{{largestCategory}} — {{largestCategoryAmount}}</b> ({{largestCategoryPercentage}}% of total revenue).', {
            totalAmount: formatNumber(data.totalAmount, currency.symbol),
            invoices: data.invoices,
            largestSupplier: data.largestSupplier?.name || 'N/A',
            largestSupplierAmount: formatNumber(data.largestSupplier?.amount || 0, currency.symbol),
            largestSupplierPercentage: numeral(data.largestSupplier?.percentage || 0).format('0.[00]'),
            largestCategory: data.largestCategory?.name || 'N/A',
            largestCategoryAmount: formatNumber(data.largestCategory?.amount || 0, currency.symbol),
            largestCategoryPercentage: numeral(data.largestCategory?.percentage || 0).format('0.[00]'),
            from: moment(from).format('YYYY-MM-DD'),
            to: moment(to).format('YYYY-MM-DD'),
          })
        }} />
      </div>
    </div>
  )
}

export default RevenueByCategoryTips
